import { useMemo } from "react";

// Mastercard-only fork of VAMP/hooks/useMonitoringProgramProgress.js — the VAMP original computes
// the finish date as a static offset from the breach start month, which is wrong whenever a
// re-breach/tier escalation happens before the first clean month (finish must be a rolling
// projection from *now*, not from the original start). VAMP's own progress bar is out of scope
// here, so this fork only changes ECP/EFM's copy — VAMP is left untouched.
const useMonitoringProgramProgress = ({ data }) => {
  const status = data?.current_status?.status;

  const startMonthObj = data?.timeline?.find(
    (month) => month.recovery_month_number === 1,
  );
  const startMonth = startMonthObj?.month;
  const startYear = startMonthObj?.year;

  // Finish is a rolling projection from *now* (total - current clean months still needed),
  // not a static offset from the breach start — a re-breach/escalation before the first clean
  // month must not pull the projected exit date earlier than it really is.
  const recoveryTotal = data?.current_status?.recovery_progress?.total ?? 3;
  const recoveryCurrent = data?.current_status?.recovery_progress?.current ?? 0;
  const remainingCleanMonths = recoveryTotal - recoveryCurrent;

  const anchorMonth = data?.current_month?.month;
  const anchorYear = data?.current_month?.year;
  const endDateObj =
    anchorMonth && anchorYear
      ? new Date(anchorYear, anchorMonth - 1 + remainingCleanMonths, 1)
      : null;
  // Back to a 1-indexed month number for the timeline lookups below (bar width, edge offsets).
  const endMonth = endDateObj ? endDateObj.getMonth() + 1 : null;

  const programProgress =
    recoveryCurrent > 0 ? ((recoveryCurrent - 1) / recoveryTotal) * 100 : 5;
  const totalLength = data?.timeline?.length;
  const programLength = endMonth && startMonth ? endMonth - startMonth + 1 : 0;
  const startDate = startMonth ? new Date(startYear, startMonth - 1) : null;
  const endDate = endDateObj;

  const isShowProgressBar =
    status === "under_monitoring" &&
    startMonth &&
    endMonth &&
    programLength > 0;

  const timeLineSegments = useMemo(() => {
    const segments = [];

    if (startMonth > 1) {
      segments.push({
        progress: 0,
        width: ((startMonth - 1) / totalLength) * 100 + "%",
      });
    }
    segments.push({
      progress: programProgress,
      width: (programLength / totalLength) * 100 + "%",
    });

    if (endMonth < totalLength) {
      segments.push({
        progress: 0,
        width: ((totalLength - endMonth) / totalLength) * 100 + "%",
      });
    }
    return segments;
  }, [data]);

  const edgesIntervalWidth = (programLength / totalLength) * 100 + "%";
  const edgesStartOffset = ((startMonth - 1) / totalLength) * 100 + "%";
  const isFinishTextHidden = endMonth >= totalLength;

  return {
    isShowProgressBar,
    timeLineSegments,
    startDate,
    endDate,
    edgesIntervalWidth,
    edgesStartOffset,
    isFinishTextHidden,
  };
};

export default useMonitoringProgramProgress;
